import { FC, Fragment, useState } from "react";
import useClients from "../../../hooks/api-calls/useClients";
import useParks from "../../../hooks/api-calls/useParks";
import DateField from "../../../components/forms/DateField";
import { isEqual } from "lodash";
import CustomSelect from "../../../components/forms/CustomSelect";
import { Accordion, Button } from "rizzui";
import { DocumentsService } from "../../../services/document/documentsService";
import useFilters from "../../../hooks/useFilters";
import { categoryOptions } from "../../../utils/SelectorMapping";
import { toast } from "sonner";
import useHandleErrors from "../../../hooks/useHandleErrors";
import moment from "moment";
import cn from "../../../utils/classNames";
import { IoIosArrowDown } from "react-icons/io";

interface GeneratorCustomReportProps {
    doRefresh: boolean;
    setDoRefresh: (value: boolean) => void;
}

const GeneratorCustomReport: FC<GeneratorCustomReportProps> = ({ doRefresh, setDoRefresh }) => {

    // STATES

    const [loading, setLoading] = useState(false);
    const service = new DocumentsService();
    

    // HOOKS

    const { getClientsList } = useClients();
    const { getParksList } = useParks();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters();
    const { handleErrors } = useHandleErrors();

    // FUNCTIONS



    //----------------------------------------------------------------------------------------------------------------------------
    /**
     * @EN FUNCTION TO GENERATE REPORT
     * @ES FUNCION PARA GENERAR INFORME
     * 
     * @param filters
     */
    //----------------------------------------------------------------------------------------------------------------------------
    const generateReport = async (filters: any) => {
        if(!validateFilters(filters)) return;
        setLoading(true);

        try {
            // Create a copy of filters and format dates for backend
            const formattedFilters = { ...filters };
            if (formattedFilters.filter_filters?.between_dates) {
                const { startDate, endDate, type } = formattedFilters.filter_filters.between_dates;
                
                formattedFilters.filter_filters.between_dates = {
                    startDate: startDate ? moment(startDate, 'DD/MM/YYYY').format('YYYY-MM-DD') : null,
                    endDate: endDate ? moment(endDate, 'DD/MM/YYYY').format('YYYY-MM-DD') : null,
                    type: type
                };
            }

            const response = await (await service.generateCustomReport(formattedFilters)).getResponseData();
            if(response.success) {
                toast.success('Informe generado correctamente');
            }
            else {
                handleErrors(response);
            }
            setDoRefresh(!doRefresh);
            resetFilters();
        } catch (error) {
            handleErrors(error);
            resetFilters();
        } finally {
            setLoading(false);
            resetFilters();
        }
    };
    //----------------------------------------------------------------------------------------------------------------------------);


    //----------------------------------------------------------------------------------------------------------------------------
    /**
     * @EN FUNCTION TO VALIDATE FILTERS
     * @ES FUNCION PARA VALIDAR FILTROS
     * 
     * @param filters
     */
    const validateFilters = (filters: any) => {
        const { client, park, type, between_dates } = filters.filter_filters || {};
      
        if (!client || client.length === 0) {
          toast.error("Debes seleccionar al menos un cliente.");
          return false;
        }
      
        if (!park || park.length === 0) {
          toast.error("Debes seleccionar al menos un parque.");
          return false;
        }
      
        if (!type || type.length === 0) {
          toast.error("Debes seleccionar el tipo de informe.");
          return false;
        }
      
        if (
          !between_dates ||
          !between_dates.startDate ||
          !between_dates.endDate
        ) {
          toast.error("Debes seleccionar un rango de fechas.");
          return false;
        }
      
        return true;
    };
    //----------------------------------------------------------------------------------------------------------------------------


    // RENDER

    return (
        <Fragment>
            <Accordion
                defaultOpen
                key={'generator-custom-report'}
                className=" bg-white dark:bg-gray-50 shadow-sm rounded-md p-4 mb-4 gap-4 sticky z-[70] mt-3 fs-table"
            >
                <Accordion.Header className="flex justify-start">
                    {({ open }) => (
                    <div className="flex w-full cursor-pointer items-center justify-between text-xl font-semibold">
                                            <div className='col-span-2 flex flex-col gap-2'>
                        <h2 className='text-[18px] font-bold capitalize text-start'>
                            Generador de Informes
                        </h2>

                        <span className='text-[12px] font-semibold text-gray-500 dark:text-gray-400'>
                            Seleccione los parametros para generar el informe
                        </span>

                    </div>
                        <IoIosArrowDown
                            className={cn(
                            "h-5 w-5 -rotate-90 transform transition-transform duration-300",
                            open && "-rotate-0"
                            )}
                        />
                        </div>
                    )}
                </Accordion.Header>
                <Accordion.Body>
                    <div className="grid md:grid-cols-2 grid-cols-1 gap-2 mb-4 mt-3">
                        <CustomSelect
                            isSearchable
                            id={'clientId'}
                            labelClassName="fs-table"
                            label="Clientes"
                            placeholder="Selecciona los clientes"
                            options={getClientsList()}
                            onChange={(e: any) => { updateFilters({ client: [e.value] }) }}
                            value={
                                filters.filter_filters?.client
                                    ? getClientsList().filter((client: any) => filters.filter_filters?.client.includes(client.value))
                                    : []
                            }
                        />
                        <CustomSelect
                            isSearchable
                            id={'parkId'}
                            label="Parques Eólicos"
                            labelClassName="fs-table"
                            placeholder="Selecciona los parques"
                            options={getParksList()}
                            onChange={(e: any) => { updateFilters({ park: [e.value] }) }}
                            value={
                                filters.filter_filters?.park
                                    ? getParksList().filter((park: any) => filters.filter_filters?.park.includes(park.value))
                                    : []
                            }
                        />
                    </div>

                    <div className="grid md:grid-cols-2 grid-cols-1 gap-2 mb-4 mt-3">
                        <CustomSelect
                            isSearchable
                            id={'type'}
                            label="Tipo de Informe"
                            labelClassName="fs-table"
                            placeholder="Selecciona el tipo de informe"
                            options={categoryOptions}
                            onChange={(e: any) => { updateFilters({ type: [e.value] }) }}
                            value={
                                filters.filter_filters?.type
                                    ? categoryOptions.filter((type: any) => filters.filter_filters?.type.includes(type.value))
                                    : []
                            }
                        />
                        <DateField
                            isClearable
                            className="w-full"
                            datePickerClassName="fs-table"
                            labelClassName="fs-table"
                            label="Fecha de Reporte"
                            placeholderText="Selecciona las fechas"
                            selectedDateField={filters.filter_filters?.between_dates?.type || "reportDate"}
                            startDate={filters.filter_filters?.between_dates?.startDate ? new Date(filters.filter_filters.between_dates.startDate) : null}
                            endDate={filters.filter_filters?.between_dates?.endDate ? new Date(filters.filter_filters.between_dates.endDate) : null}
                            onChange={(date: any) => {
                                const filters_between_dates = filters.filter_filters?.between_dates;
                                const between_dates = date ? { startDate: date[0], endDate: date[1] } : null;
            
                                if (between_dates && !isEqual(filters_between_dates, between_dates)) {
                                    updateFilters({ between_dates: { startDate: date[0], endDate: date[1], type: date[2] } });
                                } else if (!between_dates && filters_between_dates) {
                                    updateFilters({ between_dates: null });
                                }
                            }}
                        />
                    </div>


                    <div className='flex justify-center p-2'>
                        <Button
                        onClick={() => generateReport(filters)}
                        color='primary'
                        className="fs-table"
                        disabled={loading}
                        >
                            {loading ? 'Generando...' : 'Generar Informe'}
                        </Button>

                        <Button
                        onClick={() => resetFilters()}
                        color='danger'
                        className=' ml-2 fs-table'
                        disabled={loading}
                        >
                            Borrar campos
                        </Button>
                    </div>
                </Accordion.Body>
            </Accordion>
        </Fragment>
    );
};

export default GeneratorCustomReport;